// src/hooks/useAnnouncementComments.js
import { useCallback } from "react";
import { mergeCommentsKeepOptimistic } from "../Utils/AnnouncementsUtil";

export default function useAnnouncementComments({
  announcementId,
  selectedCompanyId,
  user,
  emptyDraft,
  draftRef,
  setMany,
  addComment,
  likeComment,
  showToast,
}) {
  const addOptimisticComment = useCallback(
    ({ parentId = null, body }) => {
      const trimmed = (body || "").trim();
      if (!trimmed) return null;

      const tempId = `tmp-${Date.now()}-${Math.random()
        .toString(16)
        .slice(2)}`;

      const senderName = `${user?.etunimi || ""} ${user?.sukunimi || ""}`.trim();
      const prev = draftRef.current || emptyDraft;
      const arr = Array.isArray(prev.comments) ? prev.comments : [];

      if (arr.some((c) => String(c?.id) === String(tempId))) return null;

      const next = arr.concat({
        id: tempId,
        body: trimmed,
        senderId: user?.uid || null,
        senderName,
        parentId: parentId || null,
        likes: 0,
        likedBy: [],
        createdAt: Date.now(),
        _optimistic: true,
      });

      next.sort((a, b) => (a?.createdAt || 0) - (b?.createdAt || 0));
      setMany({ ...prev, comments: next });

      return { tempId, trimmed, senderName, parentId: parentId || null };
    },
    [draftRef, emptyDraft, setMany, user]
  );

  const replaceOptimisticComment = useCallback(
    ({ tempId, saved, fallback }) => {
      const prev = draftRef.current || emptyDraft;
      const arr = Array.isArray(prev.comments) ? prev.comments : [];
      const savedId = saved?.id != null ? String(saved.id) : null;

      let next = arr.filter((c) => String(c?.id) !== String(tempId));

      if (savedId && next.some((c) => String(c?.id) === savedId)) {
        next.sort((a, b) => (a?.createdAt || 0) - (b?.createdAt || 0));
        setMany({ ...prev, comments: next });
        return;
      }

      next = next.concat({
        id: saved?.id || tempId,
        body: saved?.body || fallback.body,
        senderId: saved?.senderId ?? fallback.senderId ?? user?.uid ?? null,
        senderName: saved?.senderName ?? fallback.senderName,
        parentId: saved?.parentId ?? fallback.parentId ?? null,
        likes: saved?.likes ?? 0,
        likedBy: Array.isArray(saved?.likedBy) ? saved.likedBy : [],
        createdAt:
          typeof saved?.createdAt === "number" ? saved.createdAt : Date.now(),
      });

      next.sort((a, b) => (a?.createdAt || 0) - (b?.createdAt || 0));
      setMany({ ...prev, comments: next });
    },
    [draftRef, emptyDraft, setMany, user?.uid]
  );

  const removeOptimisticComment = useCallback(
    (tempId) => {
      const prev = draftRef.current || emptyDraft;
      setMany({
        ...prev,
        comments: (prev.comments || []).filter(
          (c) => String(c?.id) !== String(tempId)
        ),
      });
    },
    [draftRef, emptyDraft, setMany]
  );

  const applyIncomingCommentsToDraft = useCallback(
    (incomingComments) => {
      const prev = draftRef.current || emptyDraft;
      const mergedComments = mergeCommentsKeepOptimistic(
        prev.comments,
        incomingComments
      );

      setMany({
        ...prev,
        comments: mergedComments,
      });

      return mergedComments;
    },
    [draftRef, emptyDraft, setMany]
  );

  const handleSendComment = useCallback(
    async (body) => {
      if (!announcementId) return;
      if (!selectedCompanyId) return;

      const opt = addOptimisticComment({ parentId: null, body });
      if (!opt) return;

      try {
        const saved = await addComment(selectedCompanyId, announcementId, {
          body: opt.trimmed,
          senderId: user?.uid || null,
          senderName: opt.senderName,
        });

        replaceOptimisticComment({
          tempId: opt.tempId,
          saved,
          fallback: {
            body: opt.trimmed,
            senderId: user?.uid || null,
            senderName: opt.senderName,
            parentId: null,
          },
        });
      } catch (e) {
        console.error("Kommentin lisäys epäonnistui:", e);
        removeOptimisticComment(opt.tempId);
        showToast?.("Kommentin lisäys epäonnistui", "error", 2500);
      }
    },
    [
      announcementId,
      selectedCompanyId,
      addOptimisticComment,
      addComment,
      replaceOptimisticComment,
      removeOptimisticComment,
      showToast,
      user,
    ]
  );

  const handleReplySubmit = useCallback(
    async (parentId, body) => {
      if (!announcementId) return;
      if (!selectedCompanyId) return;

      const opt = addOptimisticComment({ parentId, body });
      if (!opt) return;

      try {
        const saved = await addComment(selectedCompanyId, announcementId, {
          body: opt.trimmed,
          parentId,
          senderId: user?.uid || null,
          senderName: opt.senderName,
        });

        replaceOptimisticComment({
          tempId: opt.tempId,
          saved,
          fallback: {
            body: opt.trimmed,
            senderId: user?.uid || null,
            senderName: opt.senderName,
            parentId,
          },
        });
      } catch (e) {
        console.error("Vastauksen lisäys epäonnistui:", e);
        removeOptimisticComment(opt.tempId);
        showToast?.("Vastauksen lisäys epäonnistui", "error", 2500);
      }
    },
    [
      announcementId,
      selectedCompanyId,
      addOptimisticComment,
      addComment,
      replaceOptimisticComment,
      removeOptimisticComment,
      showToast,
      user,
    ]
  );

  const handleLike = useCallback(
    async (commentId) => {
      if (!announcementId || !commentId) return;
      if (!selectedCompanyId) return;

      const uid = user?.uid || null;
      if (!uid) return;

      const userName = `${user?.etunimi || ""} ${user?.sukunimi || ""}`.trim();
      const prev = draftRef.current || emptyDraft;
      const arr = Array.isArray(prev.comments) ? prev.comments : [];

      const next = arr.map((c) => {
        if (String(c?.id) !== String(commentId)) return c;
        const likedBy = Array.isArray(c.likedBy) ? c.likedBy : [];
        const already = likedBy.includes(userName);
        const nextLikedBy = already
          ? likedBy.filter((n) => n !== userName)
          : [...likedBy, userName];
        return { ...c, likedBy: nextLikedBy, likes: nextLikedBy.length };
      });

      setMany({ ...prev, comments: next });

      try {
        await likeComment(selectedCompanyId, announcementId, commentId, uid, userName);
      } catch (e) {
        console.warn("Like persist failed:", e);
        showToast?.("Tykkäyksen tallennus epäonnistui", "error", 2500);
      }
    },
    [
      announcementId,
      selectedCompanyId,
      user,
      draftRef,
      emptyDraft,
      setMany,
      likeComment,
      showToast,
    ]
  );

  return {
    addOptimisticComment,
    replaceOptimisticComment,
    removeOptimisticComment,
    applyIncomingCommentsToDraft,
    handleSendComment,
    handleReplySubmit,
    handleLike,
  };
}